import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/images/logo.png';
import Button from '../components/Button';
import PasswordInput from '../components/PasswordInput';

type Stage = 'verifying' | 'form' | 'success' | 'invalid';

const ResetPasswordScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // HashRouter puts query params inside the hash: /#/reset-password?oobCode=...
  // useSearchParams() only reads params from the real URL, not the hash fragment.
  // So we parse both sources and prefer the hash-based params.
  const hashSearch = window.location.hash.includes('?')
    ? window.location.hash.slice(window.location.hash.indexOf('?'))
    : '';
  const hashParams = new URLSearchParams(hashSearch);
  const oobCode = hashParams.get('oobCode') || searchParams.get('oobCode') || '';

  const [stage, setStage] = useState<Stage>('verifying');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!oobCode) {
      setStage('invalid');
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((recoveredEmail) => {
        setEmail(recoveredEmail);
        setStage('form');
      })
      .catch(() => {
        setStage('invalid');
      });
  }, [oobCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError(t('reset_password.err_short'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('reset_password.err_mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStage('success');
    } catch (err: any) {
      if (err.code === 'auth/expired-action-code') {
        setError(t('reset_password.err_expired'));
      } else if (err.code === 'auth/invalid-action-code') {
        setError(t('reset_password.err_invalid'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('reset_password.err_weak'));
      } else {
        setError(t('reset_password.err_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src={logo} alt="VisaConnect" className="h-12 mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">VisaConnect</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-8">
          {stage === 'verifying' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4" />
              <p className="text-gray-500 text-sm">{t('reset_password.verifying')}</p>
            </div>
          )}

          {stage === 'invalid' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">🔗</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('reset_password.invalid_title')}</h2>
              <p className="text-gray-500 text-sm mb-6">
                {t('reset_password.invalid_body')}
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => navigate('/sign-in')}
              >
                {t('reset_password.back_to_login')}
              </Button>
            </div>
          )}

          {stage === 'form' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-1">{t('reset_password.form_title')}</h2>
                {email && (
                  <p className="text-sm text-gray-500">
                    {t('reset_password.for_email')}{' '}<span className="font-medium text-gray-700">{email}</span>
                  </p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reset_password.new_password')}
                  </label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setError('');
                    }}
                    placeholder={t('reset_password.placeholder_new')}
                    disabled={submitting}
                    showRequirements={true}
                    password={password}
                    aria-label={t('reset_password.aria_new')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('reset_password.confirm_password')}
                  </label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setError('');
                    }}
                    placeholder={t('reset_password.placeholder_confirm')}
                    disabled={submitting}
                    showRequirements={false}
                    aria-label={t('reset_password.aria_confirm')}
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    {error}
                  </p>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  className="w-full"
                  disabled={submitting}
                >
                  {submitting ? t('reset_password.saving') : t('reset_password.submit')}
                </Button>
              </form>
            </>
          )}

          {stage === 'success' && (
            <div className="text-center py-4">
              <div className="text-5xl mb-4">✅</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{t('reset_password.success_title')}</h2>
              <p className="text-gray-500 text-sm mb-6">
                {t('reset_password.success_body')}
              </p>
              <Button
                variant="primary"
                className="w-full"
                onClick={() => navigate('/sign-in')}
              >
                {t('reset_password.go_to_login')}
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          {t('reset_password.need_help')}{' '}
          <a href="mailto:support@visaconnectus.com" className="text-blue-500 hover:underline">
            {t('reset_password.contact_support')}
          </a>
        </p>
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
